import { Navigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { AppShell } from '../components/AppShell'
import { Button } from '../components/Button'
import { Logo } from '../components/Logo'
import { Skeleton } from '../components/Skeleton'
import { NoOrganizationPage } from '../pages/NoOrganizationPage'

function FullScreenStatus({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ground px-6">
      <Logo />
      <Skeleton className="h-2 w-48" />
      <p className="text-small text-ink-muted" role="status">
        {label}
      </p>
    </div>
  )
}

/**
 * Route guard for the authenticated area:
 *   1. no session        → /login
 *   2. session, no org   → organization onboarding
 *   3. session + org     → app shell (renders the matched child route)
 */
export function RequireAuth() {
  const { session, loading, memberships, membershipsLoading, membershipsError, refreshMemberships } =
    useAuth()

  if (loading) return <FullScreenStatus label="Checking your session…" />

  if (!session) return <Navigate to="/login" replace />

  if (membershipsLoading && memberships.length === 0) {
    return <FullScreenStatus label="Loading your workspace…" />
  }

  if (membershipsError && memberships.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ground px-6 text-center">
        <Logo />
        <h1 className="font-display text-display-m text-ink">We couldn't load your workspace</h1>
        <p className="max-w-md text-body text-ink-muted">{membershipsError}</p>
        <Button onClick={() => void refreshMemberships()}>Try again</Button>
      </div>
    )
  }

  if (memberships.length === 0) return <NoOrganizationPage />

  return (
    <AppShell />
  )
}
